import { Option, Schema as S } from "effect";
import { Field } from "foldkit/fieldValidation";

import { apiData } from "../../api/asyncData";
import { ENTRY_TYPES, RecordType } from "../../api/schemas/common";
import { Week } from "../../api/schemas/keepInTouch";
import { RecordSummary, SearchResult } from "../../api/schemas/records";
import * as AddEntry from "../../widgets/addEntry";
import * as Attributes from "../../widgets/attributes";
import { Dialog } from "../../widgets/dialog";
import * as Picker from "../../widgets/recordPicker";
import * as Tags from "../../widgets/tags";

/** A search hit; time-travel rows are records converted with their tags kept for client-side filtering. */
export const Row = S.Struct({
	...SearchResult.fields,
	tags: S.optionalKey(S.Array(S.String)),
});
export type Row = typeof Row.Type;

/** Every fetched page flattened, as v1's infinite query did. */
export const Results = S.Struct({
	items: S.Array(Row),
	nextCursor: S.Option(S.Number),
});
export type Results = typeof Results.Type;

export const SearchType = S.Literals(["all", "node", "knot"]);
export type SearchType = typeof SearchType.Type;
export const EntryFilter = S.Literals(["all", ...ENTRY_TYPES]);
export type EntryFilter = typeof EntryFilter.Type;

/** v1 `RecordProfileEditForm` in create mode: reserved keys as fields, the rest in the attribute editor. */
export const Form = S.Struct({
	name: Field(S.String),
	type: RecordType,
	subtitle: Field(S.String),
	email: Field(S.String),
	description: Field(S.String),
	tags: S.Array(S.String),
	tagBox: Tags.Model,
	tagError: S.Option(S.String),
	attributes: S.Array(Attributes.Row),
	error: S.Option(S.String),
});
export type Form = typeof Form.Type;

const notValidated = { _tag: "NotValidated", value: "" } as const;
export const emptyForm: Form = {
	name: notValidated,
	type: "person",
	subtitle: notValidated,
	email: notValidated,
	description: notValidated,
	tags: [],
	tagBox: Tags.init("add-record-tags"),
	tagError: Option.none(),
	attributes: [],
	error: Option.none(),
};

/** Home capture (R8): the picker, the add-entry widget, and the card a Reach out came from (KTD8). */
export const Capture = S.Struct({
	picker: Picker.Model,
	addEntry: AddEntry.Model,
	fromCard: S.Option(S.String),
});
export type Capture = typeof Capture.Type;

export const Model = S.Struct({
	page: S.Literal("Searched"),
	searchText: S.String,
	searchType: SearchType,
	knotType: EntryFilter,
	results: apiData(Results).schema,
	/** Insertion-ordered: merge takes the first selected record as the target (v1 `ids[0]`). */
	selected: S.Array(S.String),
	deleteDialog: Dialog.Model,
	/** The rows before an optimistic delete, restored if any delete fails. */
	snapshot: S.Option(Results),
	pendingDeletes: S.Number,
	deleteError: S.Option(S.String),
	merging: S.Boolean,
	addDialog: Dialog.Model,
	form: Form,
	saving: S.Boolean,
	/** The week's cards (KTD8): fetched on Home only, Idle on Records and under time travel. */
	keepInTouch: apiData(Week).schema,
	/** A skip or swap that failed: shown above the cards, cleared by the next action. */
	keepInTouchError: S.Option(S.String),
	capture: Capture,
	/** Recently touched records (R11): fetched beside the week on Home only. */
	recent: apiData(S.Array(RecordSummary)).schema,
});
export type Model = typeof Model.Type;
